// La pestaña "Encuestas" de la ficha del cliente (Lissette 2026-10-15).
//
// Lo que este cliente contestó, encuesta por encuesta, y un botón para copiar
// SU enlace de cualquier encuesta activa — para mandárselo por WhatsApp o por
// texto sin pasar por el correo masivo.
//
// Va en su propio archivo y no dentro de EncuestasView porque esa vista se
// carga bajo demanda, y la ficha no puede importarla de forma estática (ver
// deskte-api/tests/unit/modulosBajoDemanda.test.ts).

import { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from './Toast.jsx';
import { encuestasDelCliente, listEncuestas, enlaceDeEncuestaParaCliente } from '../api.js';
import { enIdioma, textoDeRespuesta } from '../encuestasPlantillas.js';

const muted = { color: 'var(--c-text2,#7aa3c0)', fontSize: '.76rem' };
const caja = { background: 'var(--c-surface,#0d1f30)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.8rem .95rem' };
const fecha = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export function ClientEncuestasTab({ clientId, lang }) {
  const es = lang === 'es';
  const [filas, setFilas] = useState(null);
  const [activas, setActivas] = useState([]);
  const [error, setError] = useState('');
  const [elegida, setElegida] = useState('');

  useEffect(() => {
    let vivo = true;
    setFilas(null);
    setError('');
    Promise.all([encuestasDelCliente(clientId), listEncuestas()])
      .then(([f, todas]) => {
        if (!vivo) return;
        setFilas(f);
        const a = todas.filter((x) => x.activa);
        setActivas(a);
        setElegida(a[0]?.id || '');
      })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [clientId]);

  const copiarEnlace = async () => {
    if (!elegida) return;
    try {
      const { url } = await enlaceDeEncuestaParaCliente(elegida, clientId);
      await navigator.clipboard.writeText(url);
      toast.success(es ? 'Enlace copiado. Es solo para este cliente.' : 'Link copied. It is only for this client.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (error) return <div style={{ color: 'var(--c-red,#ff4d6d)', fontSize: '.82rem' }}>{error}</div>;
  if (!filas) return <div style={muted}>{es ? 'Cargando…' : 'Loading…'}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.7rem' }}>
      {activas.length > 0 && (
        <div style={{ ...caja, display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={muted}>{es ? 'Enlace personal de:' : 'Personal link for:'}</span>
          <select className="dkt-inp" value={elegida} onChange={(e) => setElegida(e.target.value)} style={{ width: 'auto', minWidth: 180 }}>
            {activas.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
          </select>
          <button type="button" className="dkt-btn" onClick={copiarEnlace}><Copy size={13} /> {es ? 'Copiar enlace' : 'Copy link'}</button>
        </div>
      )}
      {filas.length === 0 && (
        <div style={{ ...caja, ...muted }}>
          {es ? 'Este cliente todavía no ha recibido encuestas.' : 'This client has not received any surveys yet.'}
        </div>
      )}
      {filas.map((f) => {
        const preguntas = f.contenido?.preguntas || [];
        return (
          <div key={f.id} style={caja}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '.5rem', flexWrap: 'wrap', marginBottom: f.respondidaAt ? '.5rem' : 0 }}>
              <strong>📋 {f.nombre}</strong>
              <span style={muted}>
                {f.respondidaAt
                  ? `${es ? 'Respondida' : 'Answered'} ${fecha(f.respondidaAt)}`
                  : f.abiertaAt
                    ? `${es ? 'Abierta, sin responder' : 'Opened, not answered'} · ${es ? 'enviada' : 'sent'} ${fecha(f.enviadaAt)}`
                    : `${es ? 'Enviada' : 'Sent'} ${fecha(f.enviadaAt)} · ${es ? 'sin abrir' : 'not opened'}`}
              </span>
            </div>
            {f.respondidaAt && preguntas.map((p) => {
              const v = textoDeRespuesta(p, f.respuestas[p.id], lang);
              return (
                <div key={p.id} style={{ fontSize: '.8rem', marginBottom: 6 }}>
                  <div style={muted}>{enIdioma(p.texto, lang)}</div>
                  <div style={{ color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap' }}>{v || '—'}</div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
